import Grid from '@mui/material/Grid2'
import {
	Box,
	Typography,
	Button,
	Card,
	CardContent,
	CardActions,
	CardHeader,
	Divider,
	CircularProgress,
	FormControlLabel,
	Switch
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useBatch } from '../hooks/useBatch'
import { useState, useEffect } from 'react'
import { calculateLifeStatus } from '../utils/calculateLifeStatus'
import { BatchSearchFilter } from '../components/BatchSearchFilter'

export const ManageBatchs = () => {
	const { batches, fetchBatches, loadingList, total } = useBatch()
	const [page, setPage] = useState(0)
	const [searchTerm, setSearchTerm] = useState('')
	const [filterType, setFilterType] = useState('batchId')
	const [filters, setFilters] = useState({ crop: '', germplasm: '', category: '', batchId: '' })
	const [includeInactive, setIncludeInactive] = useState(false)
	const [sortBy, setSortBy] = useState('date')
	const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
	const itemsPerPage = 8
	const navigate = useNavigate()

	useEffect(() => {
		// Crear objeto de filtros basado en el tipo seleccionado
		const activeFilters = {
			...(filterType === 'crop' && { crop: searchTerm }),
			...(filterType === 'germplasm' && { germplasm: searchTerm }),
			...(filterType === 'category' && { category: searchTerm }),
			...(filterType === 'batchId' && { batchId: searchTerm }),
			...(includeInactive && { includeInactive: 'true' })
		}
		
		fetchBatches(page * itemsPerPage, (page + 1) * itemsPerPage, activeFilters)
	}, [page, fetchBatches, filterType, searchTerm, includeInactive, sortBy, sortOrder])

	// Función para ordenar los lotes localmente
	const sortBatches = (batchesToSort: any[]) => {
		return [...batchesToSort].sort((a, b) => {
			let comparison = 0
			
			switch (sortBy) {
				case 'date':
					comparison = new Date(a.date).getTime() - new Date(b.date).getTime()
					break
				case 'crop':
					comparison = a.crop.localeCompare(b.crop)
					break
				case 'germplasm':
					comparison = a.germplasm.localeCompare(b.germplasm)
					break
				case 'category':
					comparison = a.category.localeCompare(b.category)
					break
				default:
					comparison = 0
			}
			
			return sortOrder === 'asc' ? comparison : -comparison
		})
	}

	const sortedBatches = sortBatches(batches)

	const handleSearch = () => {
		// Resetear página al hacer una nueva búsqueda
		setPage(0)
		
		// Actualizar filtros y ejecutar búsqueda
		const activeFilters = {
			...(filterType === 'crop' && { crop: searchTerm }),
			...(filterType === 'germplasm' && { germplasm: searchTerm }),
			...(filterType === 'category' && { category: searchTerm }),
			...(filterType === 'batchId' && { batchId: searchTerm }),
			...(includeInactive && { includeInactive: 'true' })
		}
		
		setFilters({
			crop: filterType === 'crop' ? searchTerm : '',
			germplasm: filterType === 'germplasm' ? searchTerm : '',
			category: filterType === 'category' ? searchTerm : '',
			batchId: filterType === 'batchId' ? searchTerm : ''
		})
		
		fetchBatches(0, itemsPerPage, activeFilters)
	}

	const handleNextPage = () => {
		setPage((prevPage) => prevPage + 1)
	}

	const handlePreviousPage = () => {
		if (page > 0) {
			setPage((prevPage) => prevPage - 1)
		}
	}

	const handleManageBatch = (batchId) => {
		navigate('/home/administrar-lote', { state: { batchId } })
	}

	const handleSortOrderToggle = () => {
		setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc')
	}

	return (
		<Box p={3}>
			<Typography variant="h4" align="center" gutterBottom>
				Manejo de Lotes
			</Typography>

			<BatchSearchFilter
				searchTerm={searchTerm}
				filterType={filterType}
				sortBy={sortBy}
				sortOrder={sortOrder}
				onSearchTermChange={setSearchTerm}
				onFilterTypeChange={setFilterType}
				onSortByChange={setSortBy}
				onSortOrderToggle={handleSortOrderToggle}
				onSearch={handleSearch}
			/>

			<Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
				<FormControlLabel
					control={
						<Switch
							checked={includeInactive}
							onChange={(e) => setIncludeInactive(e.target.checked)}
							color="primary"
						/>
					}
					label="Incluir lotes desactivados"
				/>
			</Box>

			{loadingList ? (
				<Box display="flex" justifyContent="center" alignItems="center" height="400px">
					<CircularProgress />
				</Box>
			) : batches.length === 0 ? (
				<Box textAlign="center" p={5}>
					<Typography variant="h6" color="textSecondary">
						No se encontraron lotes con los filtros seleccionados
					</Typography>
				</Box>
			) : (
				<Grid container spacing={2}>
					{sortedBatches.map((lote) => {
						const title = `No. de Lote: ${lote.batchId}`
						const status = calculateLifeStatus(lote.date, lote.expiration)
						const statusColor = {
							success: '#4caf50',
							warning: '#ff9800',
							error: '#f44336'
						}[status]

						return (
							<Grid key={lote.uid} size={{ xs: 12, sm: 6, md: 3 }}>
								<Card
									variant="outlined"
									sx={{
										mb: 2,
										bgcolor: lote.active ? 'background.paper' : 'grey.100',
										boxShadow: 2,
										borderRadius: 2,
										position: 'relative',
										opacity: lote.active ? 1 : 0.7,
										border: lote.active ? undefined : '2px dashed #ccc'
									}}
								>
									{!lote.active && (
										<Box
											sx={{
												position: 'absolute',
												top: 8,
												left: 8,
												backgroundColor: 'error.main',
												color: 'white',
												px: 1,
												py: 0.5,
												borderRadius: 1,
												fontSize: '0.75rem',
												fontWeight: 'bold',
												zIndex: 2
											}}
										>
											DESACTIVADO
										</Box>
									)}
									<Box
										sx={{
											width: 20,
											height: 20,
											borderRadius: '50%',
											backgroundColor: statusColor,
											position: 'absolute',
											top: 16,
											right: 16,
											zIndex: 1
										}}
									/>
									<CardHeader
										title={title}
										subheader={`Fecha de Ingreso: ${new Date(lote.date).toLocaleDateString()}`}
										titleTypographyProps={{ variant: 'body1' }}
									/>
									<Divider />
									<CardContent>
										<Typography variant="body2" mb={1}>
											<strong>Cultivo:</strong> {lote.crop}
										</Typography>
										<Typography variant="body2" mb={1}>
											<strong>Categoría:</strong> {lote.category}
										</Typography>
										<Typography variant="body2" mb={1}>
											<strong>Germoplasma:</strong> {lote.germplasm}
										</Typography>
										<Typography variant="body2" mb={1}>
											<strong>Propietario:</strong> {lote.owner}
										</Typography>
										<Typography variant="body2" mb={1}>
											<strong>Procedencia:</strong> {lote.origin}
										</Typography>
										<Typography variant="body2">
											<strong>No. Tarjeta:</strong> {lote.cardNumber || 'N/A'}
										</Typography>
									</CardContent>
									<CardActions sx={{ justifyContent: 'center' }}>
										<Button
											variant="contained"
											size="small"
											onClick={() => handleManageBatch(lote.uid)}
										>
											Administrar Lote
										</Button>
									</CardActions>
								</Card>
							</Grid>
						)
					})}
				</Grid>
			)}

			<Box display="flex" justifyContent="space-between" mt={2} alignItems="center">
				<Typography variant="body2" color="textSecondary">
					Mostrando {batches.length} de {total} lotes
				</Typography>
				<Box>
					<Button 
						variant="outlined" 
						disabled={page === 0 || loadingList} 
						onClick={handlePreviousPage}
						sx={{ mr: 1 }}
					>
						Página Anterior
					</Button>
					<Button 
						variant="outlined" 
						disabled={batches.length < itemsPerPage || loadingList} 
						onClick={handleNextPage}
					>
						Página Siguiente
					</Button>
				</Box>
			</Box>
		</Box>
	)
}
